import { reflectionStorage, store } from '@/store'
import { SelectedRoleEnum } from '@/store/types'
import { AdminClassesMap, RoleItem, SemesterInfo } from '@/types'
import { isArray, isEmpty, isNil, omit } from '@seiue/util'
import {
  handleInputValue,
  hasComma,
  IParams,
} from 'packages/components/DataTable'
import { User } from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'

/**
 * 安全解析 JSON 字符串为对象数组
 *
 * @param dataStr - 原始 JSON 字符串
 * @param options - 配置项
 * @param options.silent - 是否静默模式（不打印日志）
 * @returns 解析后的数组（失败返回空数组）
 */
export const parseData = (
  dataStr: string | null | undefined,
  options: { silent?: boolean } = {}
) => {
  const { silent = false } = options

  if (!dataStr) {
    return []
  }

  if (typeof dataStr !== 'string') {
    !silent && console.warn('[parseData] 参数不是字符串')
    return []
  }

  // 尝试处理非法换行符（比如直接粘贴进 JSON 的多行字符串）
  const safeStr = dataStr.replace(/(\r?\n)+/g, '\\n')

  try {
    const parsed = JSON.parse(safeStr)

    if (!Array.isArray(parsed)) {
      !silent &&
        console.warn('[parseData] 解析成功但结果不是数组:', typeof parsed)
      return []
    }

    return parsed
  } catch (error: any) {
    !silent && console.error('[parseData] JSON 解析失败:', error.message)
    !silent &&
      console.debug('[parseData] 原始字符串前 200 字符:', dataStr.slice(0, 200))
    return []
  }
}

/**
 * 更新 storage 的 reflection数据，
 *
 * @param param0 - 参数
 * @param param0.modifyKeys - 需要修改的 key
 * @param param0.newData - 新数据
 */
export const updateReflection = ({
  modifyKeys,
  newData,
}: {
  modifyKeys: (keyof User)[]
  newData: {
    [key in keyof User]?: AnyType
  }
}) => {
  const oldReflection = store.get(reflectionStorage)
  store.set(reflectionStorage, {
    ...omit(oldReflection, modifyKeys),
    ...newData,
  } as User)
}

/**
 * 获取角色列表
 *
 * @param semesterInfo - 学期
 * @returns roleList
 */
export const getRoleList = (semesterInfo: SemesterInfo | undefined) => {
  if (!semesterInfo) {
    return []
  }

  const isMentor = semesterInfo.is_mentor === 1
  const isDean = semesterInfo.is_dean === 1
  const isEducationalSupervisor = semesterInfo.is_educational_supervisor === 1
  const isSchoolLeaders = semesterInfo.is_school_leaders === 1
  const isAcademicAffairs = semesterInfo.is_academic_affairs === 1
  const isYuke = semesterInfo.is_yuke === 1
  const isAdminclassMentor = semesterInfo.is_adminclass_mentor === 1

  const roleList = [
    {
      key: SelectedRoleEnum.Mentor,
      label: '导师',
      visible: isMentor,
    },
    {
      key: SelectedRoleEnum.EducationalSupervisor,
      label: '书院',
      visible: isEducationalSupervisor,
    },
    {
      key: SelectedRoleEnum.Dean,
      label: '学院',
      visible: isDean,
    },
    {
      key: SelectedRoleEnum.Yuke,
      label: '预科部',
      visible: isYuke,
    },
    {
      key: SelectedRoleEnum.SchoolLeaders,
      label: '全校',
      visible: isSchoolLeaders,
    },
    {
      key: SelectedRoleEnum.AcademicAffairs,
      label: '教务',
      visible: isAcademicAffairs,
    },
    {
      key: SelectedRoleEnum.AdminclassMentor,
      label: '行政班',
      visible: isAdminclassMentor,
    },
  ].filter((r) => r.visible)

  return roleList
}

export const getAdminclassMentorRoleList = (semesterInfo: SemesterInfo) => {
  // 是否是行政班导师
  const isAdminclassMentor = semesterInfo.is_adminclass_mentor === 1

  const adminclass_map = parseData(
    semesterInfo.adminclass_map || '[]'
  ) as AdminClassesMap[]

  const adminclassRoleList: RoleItem[] = isAdminclassMentor
    ? adminclass_map.map((a) => ({
        key: `${SelectedRoleEnum.AdminclassMentor}-${a.graduates_in_term_name}`,
        label: `行政班-${a.graduates_in_term_name}`,
        visible: true,
        isAdminclassMentor: true,
        graduatesInTermName: a.graduates_in_term_name,
      }))
    : []

  const adminclassTermNames = isAdminclassMentor
    ? adminclass_map.map((a) => a.graduates_in_term_name)
    : []

  return { adminclassRoleList, adminclassTermNames }
}

/**
 * 获取角色列表
 *
 * @param semesterInfo - 学期
 * @returns roleList
 */
export const getRoleListForHeader = (
  semesterInfo: SemesterInfo | undefined
) => {
  if (!semesterInfo) {
    return []
  }

  const isMentor = semesterInfo.is_mentor === 1
  const isDean = semesterInfo.is_dean === 1
  const isEducationalSupervisor = semesterInfo.is_educational_supervisor === 1
  const isSchoolLeaders = semesterInfo.is_school_leaders === 1
  const isAcademicAffairs = semesterInfo.is_academic_affairs === 1
  const isYuke = semesterInfo.is_yuke === 1

  const { adminclassRoleList } = getAdminclassMentorRoleList(semesterInfo)

  const roleList: RoleItem[] = [
    {
      key: SelectedRoleEnum.Mentor,
      label: '导师',
      visible: isMentor,
    },
    {
      key: SelectedRoleEnum.EducationalSupervisor,
      label: '书院',
      visible: isEducationalSupervisor,
    },
    {
      key: SelectedRoleEnum.Dean,
      label: '学院',
      visible: isDean,
    },
    {
      key: SelectedRoleEnum.Yuke,
      label: '预科部',
      visible: isYuke,
    },
    {
      key: SelectedRoleEnum.SchoolLeaders,
      label: '全校',
      visible: isSchoolLeaders,
    },
    {
      key: SelectedRoleEnum.AcademicAffairs,
      label: '教务',
      visible: isAcademicAffairs,
    },
    ...adminclassRoleList,
  ].filter((r) => r.visible)

  return roleList
}

// 将驼峰转下划线，保证 query 里的 key 和数据字段对齐
const camelToSnake = (key: string) =>
  key.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase())

/**
 * 过滤数据
 *
 * @param param0 - 参数
 * @param param0.data - 数据
 * @param param0.query - 查询条件
 * @param param0.rangeQueryKeys - 范围查询
 * @returns 过滤后的数据
 */
export const filterData = <T extends any>({
  data,
  query,
}: {
  data: T[]
  query: IParams
}): T[] => {
  if (isEmpty(query)) {
    return data
  }

  return data.filter((item) => {
    for (const key in query) {
      const value = query[key]

      // 判断是否是范围条件
      const match = key.match(/^(.+)_(egt|elt)$/)
      if (match) {
        const [, baseKey, op] = match
        const snakeKey = camelToSnake(baseKey)

        const itemValue = item[snakeKey as keyof T]
        if (isNil(itemValue)) return false

        if (op === 'egt' && value != null && !(itemValue >= value)) return false
        if (op === 'elt' && value != null && !(itemValue <= value)) return false
      } else {
        // 普通等值匹配
        const snakeKey = camelToSnake(key)
        const itemValue = item[snakeKey as keyof T]

        if (typeof itemValue === 'string') {
          if (!itemValue.includes(value)) return false
        } else if (isArray(value)) {
          if (!value.includes(itemValue)) return false
        } else {
          if (item[snakeKey as keyof T] !== value) return false
        }
      }
    }
    return true
  })
}

/**
 * 转换参数：
 * - 指定 keys 使用 handleInputValue 处理
 * - 所有 key 驼峰转下划线
 */
export const transformParams = ({
  params,
  keysToHandle = [],
}: {
  params: IParams
  keysToHandle: string[]
}): IParams => {
  return Object.entries(params)
    .map(([key, value]) => {
      if (keysToHandle.includes(key) && hasComma(value)) {
        // 如果 key 在指定集合中且值包含逗号，处理值，key 后面加上 _in
        return {
          [`${key}In`.replace(/([A-Z])/g, '_$1').toLowerCase()]:
            handleInputValue(value),
        }
      }
      // 普通驼峰转下划线
      return { [key.replace(/([A-Z])/g, '_$1').toLowerCase()]: value }
    })
    .reduce((res, item) => ({ ...res, ...item }), {})
}
